import React from "react";
import { X, Download } from "lucide-react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

// Statutory components config — shown separately from earnings/custom deductions
const STATUTORY_KEYS = {
  // Employer contributions (added to CTC)
  employer: ["pf_employer", "esic_employer", "lwf_employer", "gratuity", "nps_employer"],
  // Employee deductions
  employee: ["pf_employee", "esic_employee", "lwf_employee", "professional_tax", "tds", "income_tax", "nps_employee"],
};

const STATUTORY_LABELS = {
  pf_employer:      "PF (Employer) 12%",
  esic_employer:    "ESIC (Employer) 3.25%",
  lwf_employer:     "LWF (Employer)",
  gratuity:         "Gratuity Provision",
  nps_employer:     "NPS (Employer)",
  pf_employee:      "PF (Employee) 12%",
  esic_employee:    "ESIC (Employee) 0.75%",
  lwf_employee:     "LWF (Employee)",
  professional_tax: "Professional Tax",
  tds:              "TDS",
  income_tax:       "Income Tax",
  nps_employee:     "NPS (Employee)",
};

// UK deduction labels (Phase 4a-sweep) — applied when region !== India.
const UK_LABELS = {
  paye: "PAYE Tax", paye_tax: "PAYE Tax", tax: "PAYE Tax", income_tax: "PAYE Tax",
  ni: "National Insurance", ee_ni: "National Insurance", employee_ni: "National Insurance",
  national_insurance: "National Insurance",
  pension: "Pension", ee_pension: "Pension", employee_pension: "Pension",
  student_loan: "Student Loan", studentloan: "Student Loan",
};

function money(v) {
  if (v == null) return "—";
  return typeof v === "number" ? `£${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : String(v);
}

function Section({ title, rows, color = "text-[#00ff90]" }) {
  if (!rows?.length) return null;
  const total = rows.reduce((a, [, v]) => a + (Number(v) || 0), 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-xs uppercase tracking-wider font-medium ${color}`}>{title}</p>
        <p className={`text-xs font-bold ${color}`}>{money(total)}</p>
      </div>
      <div className="space-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
            <span className="text-sm text-white/60">{label}</span>
            <span className="text-sm text-white/80">{money(value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SalaryBreakdownModal({ payslip, onClose, region = "India" }) {
  if (!payslip) return null;

  // Region-aware label: India statutory labels, else UK terms (PAYE/NI/…).
  const isIndia = region === "India";
  const label = (k) => (isIndia ? (STATUTORY_LABELS[k] || k) : (UK_LABELS[k] || STATUTORY_LABELS[k] || k));

  const earnings    = Object.entries(payslip.earnings    || {}).filter(([, v]) => v);
  const deductions  = Object.entries(payslip.deductions  || {}).filter(([, v]) => v);
  const statutory   = Object.entries(payslip.statutory   || {}).filter(([, v]) => v);

  // Employer contributions (the CTC adders) — India concept only.
  const employerContrib = (isIndia ? statutory.filter(([k]) => STATUTORY_KEYS.employer.includes(k)) : [])
    .map(([k, v]) => [STATUTORY_LABELS[k] || k, v]);

  // Non-statutory deductions (custom) vs statutory-employee deductions.
  const allStatutoryKeys = [...STATUTORY_KEYS.employer, ...STATUTORY_KEYS.employee];
  const customDeductions = deductions.filter(([k]) => !allStatutoryKeys.includes(k))
    .map(([k, v]) => [label(k), v]);
  const statutoryDeductions = deductions.filter(([k]) => allStatutoryKeys.includes(k))
    .map(([k, v]) => [label(k), v]);

  const grossEarnings   = earnings.reduce((a, [, v]) => a + (Number(v) || 0), 0);
  const totalDeductions = deductions.reduce((a, [, v]) => a + (Number(v) || 0), 0);
  const totalEmployer   = employerContrib.reduce((a, [, v]) => a + (Number(v) || 0), 0);
  const ctc             = grossEarnings + totalEmployer;

  const handleDownload = () => {
    // Simple text download — jsPDF optional
    const lines = [
      `PAYSLIP - ${payslip.month}`,
      `Employee: ${payslip.fullName || "—"}`,
      `Designation: ${payslip.designation || "—"}`,
      "",
      "=== EARNINGS ===",
      ...earnings.map(([k, v]) => `${k}: ${money(v)}`),
      `Gross: ${money(grossEarnings)}`,
      "",
      `=== ${isIndia ? "STATUTORY DEDUCTIONS (Employee)" : "DEDUCTIONS"} ===`,
      ...statutoryDeductions.map(([k, v]) => `${k}: ${money(v)}`),
      ...customDeductions.map(([k, v]) => `${k}: ${money(v)}`),
      `Total Deductions: ${money(totalDeductions)}`,
      "",
      // Employer contributions / CTC — India only.
      ...(isIndia
        ? [
            "=== EMPLOYER CONTRIBUTIONS (CTC) ===",
            ...employerContrib.map(([k, v]) => `${k}: ${money(v)}`),
            "",
            `NET PAY: ${money(payslip.netPay)}`,
            `GROSS CTC: ${money(ctc)}`,
          ]
        : [`NET PAY: ${money(payslip.netPay)}`]),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Payslip_${(payslip.month || "").replace(/\s/g, "_")}.txt`;
    a.click();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div className="w-full max-w-3xl rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_40px_#00ff9022] flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-white">Salary Breakdown · {payslip.month}</h2>
            {payslip.fullName && <p className="text-xs text-[#00ff90]/50 mt-0.5">{payslip.fullName} · {payslip.designation}</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={handleDownload}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
              <Download size={12} /> Download
            </button>
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-6">

          {/* Summary pills */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Gross Earnings", value: money(grossEarnings), color: "#00ff90" },
              { label: "Total Deductions", value: money(totalDeductions), color: "#f87171" },
              { label: "Net Pay", value: money(payslip.netPay), color: "#60a5fa" },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
                <p className="text-lg font-bold" style={{ color }}>{value}</p>
                <p className="text-xs text-white/30 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Earnings */}
            <div className={`${card} p-4`}>
              <Section title="Earnings" rows={earnings.map(([k, v]) => [k, v])} color="text-[#00ff90]" />
            </div>

            {/* Deductions */}
            <div className={`${card} p-4 space-y-5`}>
              {statutoryDeductions.length > 0 && (
                <Section title="Statutory Deductions" rows={statutoryDeductions} color="text-yellow-400" />
              )}
              {customDeductions.length > 0 && (
                <Section title="Other Deductions" rows={customDeductions} color="text-red-400" />
              )}
            </div>
          </div>

          {/* Employer contributions — CTC view */}
          {employerContrib.length > 0 && (
            <div className={`${card} p-4`}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-purple-400/70 uppercase tracking-wider font-medium">Employer Contributions (CTC Components)</p>
                <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">Not deducted from salary</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {employerContrib.map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between py-1.5 border-b border-white/5">
                    <span className="text-sm text-white/50">{label}</span>
                    <span className="text-sm text-purple-400/80">{money(value)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                <span className="text-sm text-white/50">Gross CTC</span>
                <span className="text-sm font-bold text-purple-400">{money(ctc)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}